import Component from '@glimmer/component';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { modifier } from 'ember-modifier';

interface ActionableTranslationSignature {
  Args: {
    t: string;
    params?: {
      [key: string]: string;
    };
    links?: {
      [key: string]: {
        t: string;
        href: string;
        linkClass?: string;
        target?: string;
        rel?: string;
      };
    };
    actions?: {
      [key: string]: {
        t: string;
        onClick: () => void;
        buttonClass?: string;
      };
    };
  };
}
/**
 * ActionableTranslation
 *
 * An attempt to keep markup out of the more complicated translations while still
 * rendering semantically meaningful tags
 *
 * Accepts a main translation string arg "t" for the main translation file content
 * to render, and configuration objects for links or actions to interpolate into the
 * translation. Config object properties must include separate translation strings
 * to look up what text to use in the sort of pre-render steps where linkify and
 * buttonify methods return html safe strings to pass as params to intl.t when it
 * renders the main translation
 *
 * usage:
 *
 * (translation yaml file)
 * click_here: click here
 * some:
 *   translation:
 *     key: you can {goToGoogle} to navigate to Google or {toggle} to toggle something
 *
 * (component/template file)
 * <ActionableTranslation
 *   @t="some.translation.string"
 *   @links={{hash goToGoogle=(hash href="http://www.google.com" t="click_here")}}
 *   @actions={{hash toggle=(hash onClick=this.toggleSomething t="click_here")}}
 * />
 */
// These links and buttons render inside translated prose, so they take the
// underlined in-prose treatment rather than the standalone one.
const DEFAULT_LINK_CLASS = 'app-text-link app-text-link--inline';

class ActionableTranslation extends Component<ActionableTranslationSignature> {
  @service declare intl: IntlService;

  linkify(
    actionTranslation: string,
    href: string,
    options: { linkClass?: string; target?: string; rel?: string }
  ) {
    const { linkClass = DEFAULT_LINK_CLASS, target, rel } = options;
    const linkText = this.intl.t(actionTranslation);

    const formattedAttributes = Object.entries({ target, rel })
      .filter(([_attributeKey, attributeValue]) => attributeValue !== undefined)
      .map(([attributeKey, attributeValue]) => `${attributeKey}=${attributeValue}`)
      .join(' ');

    return `<a href="${href}" class="${linkClass}" ${formattedAttributes}>${linkText}</a>`;
  }

  buttonify(actionTranslation: string, actionName: string, buttonClass?: string) {
    const buttonText = this.intl.t(actionTranslation);
    const className = buttonClass || DEFAULT_LINK_CLASS;
    // The class value MUST stay quoted: these class names contain a space, and
    // an unquoted attribute ends at the first one — which silently turned the
    // second class into a bogus attribute and dropped it.
    return `<button data-action-name="${actionName}" class="${className}">${buttonText}</button>`;
  }

  get linkTranslationDataArgs() {
    const { links = {} } = this.args;
    return Object.entries(links).reduce<{ [key: string]: string }>((currentLinks, [key, link]) => {
      if (!link) {
        return currentLinks;
      }

      const { t, href, linkClass, target, rel } = link;
      return { ...currentLinks, [key]: this.linkify(t, href, { linkClass, target, rel }) };
    }, {});
  }

  get buttonTranslationDataArgs() {
    const { actions = {} } = this.args;
    return Object.entries(actions).reduce<{ [key: string]: string }>(
      (currentButtons, [key, action]) => {
        if (!action) {
          return currentButtons;
        }

        return { ...currentButtons, [key]: this.buttonify(action.t, key, action.buttonClass) };
      },
      {}
    );
  }

  // linkify/buttonify return raw markup strings rather than SafeStrings on
  // purpose: a SafeString interpolated into an ICU message makes formatjs return
  // message parts, which render comma-joined. The `htmlSafe: true` below marks the
  // composed result safe instead.
  get translationDataArgs() {
    const { params = {} } = this.args;
    return {
      ...this.linkTranslationDataArgs,
      ...this.buttonTranslationDataArgs,
      ...params,
      htmlSafe: true,
    };
  }

  get content() {
    return this.intl.t(this.args.t, this.translationDataArgs);
  }

  onMount = modifier((element: HTMLElement) => {
    const clickHandler = (event: Event) => {
      if (event.target instanceof HTMLElement) {
        const button = event.target.closest('button');
        if (button) {
          event.preventDefault();
          event.stopPropagation();
          if (this.args.actions) {
            const action = this.args.actions[<string>button.getAttribute('data-action-name')];
            if (action?.onClick) {
              action.onClick();
            }
          }
        }
      }
    };

    element.addEventListener('click', clickHandler);

    return () => {
      element.removeEventListener('click', clickHandler);
    };
  });

  <template>
    <span class="app-rich-text" {{this.onMount}}>{{this.content}}</span>
  </template>
}

export default ActionableTranslation;
